// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useVisibleSections from './useVisibleSections'; // Adjust the path as necessary
import logo from '../assets/Logo.png';
import '../styles/Navbar.css'; // Adjust the path as necessary
import { useAffiliate } from '../contexts/AffiliateContext';

const sectionIds = ['how-it-works', 'testimonials'];

const Navbar = () => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const { isAuthenticated, logout } = useAffiliate();

  const isHome = pathname === '/';
  const isInterview = pathname === '/interview';
  const isFeedback = pathname === '/feedback';
  const isSessionEnded = pathname === '/session-ended';
  const isSetup = pathname === '/setup';
  const isAffiliateDashboard = pathname === '/affiliate/dashboard';

  const isAffiliatePage = pathname.startsWith('/affiliate');

  const hideNavbar = isInterview || isFeedback || isSessionEnded;

  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const scrollContainerRef = useRef();
  const visibleSections = useVisibleSections(sectionIds, scrollContainerRef);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is an affiliate
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      try {
        const response = await fetch('/api/affiliate/affiliates/me/', {
          credentials: 'include',
        });
        setIsAffiliate(response.ok);
      } catch (err) {
        setIsAffiliate(false);
      }
    };
    checkAffiliateStatus();
  }, []);

  // Update active section based on what is visible
  useEffect(() => {
    if (visibleSections.length > 0) {
      setActiveSection(visibleSections[0]); // first visible section gets priority
    }
  }, [visibleSections]);

  // Scroll to hash on route change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');

      const tryScroll = () => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          setTimeout(tryScroll, 100);
        }
      };

      tryScroll();
    }
  }, [location.hash]);

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home: manually scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollToTop: true } });
    }
    setExpanded(false);
  };

  if (hideNavbar) return null;

  return (
    <nav className={`navbar${expanded ? ' navbar-expanded' : ''} ${isHome ? '' : 'setup-only'}`}>
      <div className='nav-logo'>
        <Link to='/' onClick={handleHomeClick} className='logo-link'>
          <img src={logo} alt='AURON' className='logo-img' />
        </Link>
      </div>
      {isMobile && (
        <button
          className='mobile-menu-btn'
          aria-label='Toggle navigation menu'
          onClick={() => setExpanded((prev) => !prev)}
        >
          <span className='hamburger'></span>
        </button>
      )}
      <div className='nav-right'>
        {isAffiliateDashboard && isAuthenticated ? (
          <button className='get-started-btn' onClick={logout}>
            Logout
          </button>
        ) : (
          <>
            <Link to='/' onClick={handleHomeClick} className='nav-link'>
              Home
            </Link>

            {isHome && (
              <>
                <a
                  className={`nav-link internal-link ${
                    activeSection === 'how-it-works' ? 'active' : ''
                  }`}
                  href='#how-it-works'
                  onClick={() => setExpanded(false)}
                >
                  How it works
                  {activeSection === 'how-it-works' && (
                    <i className='fas fa-check-circle active-icon'></i>
                  )}
                </a>

                <a
                  className={`nav-link internal-link ${
                    activeSection === 'testimonials' ? 'active' : ''
                  }`}
                  href='#testimonials'
                  onClick={() => setExpanded(false)}
                >
                  Testimonials
                  {activeSection === 'testimonials' && (
                    <i className='fas fa-check-circle active-icon'></i>
                  )}
                </a>
              </>
            )}

            <Link to='/how-to-use' className='nav-link' onClick={() => setExpanded(false)}>
              How to Use
            </Link>

            <Link
              to='/affiliate/dashboard'
              className={`nav-link ${isAffiliatePage ? 'active' : ''}`}
              onClick={() => setExpanded(false)}
              style={{ display: isSetup ? 'none' : undefined }}
            >
              Affiliate Dashboard
            </Link>

            {!isSetup && (
              <button
                className='get-started-btn'
                onClick={() => {
                  setExpanded(false);
                  navigate('/setup');
                }}
              >
                Get Started
              </button>
            )}
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
